import { defineStore } from 'pinia'
import { STORE_ID } from '~/constants/store'
import { useAuthStore } from '~/store'
import { getOrderHistory } from '~/composables/api'
import { isSameDay } from 'date-fns'
import type { OrderHistoryState as State, OrderStatus } from '~/types/orderHistory'
import type { PastOrder } from '~/types/api/data-contracts'
import { isExpectedApiNetworkControlError } from '~/utils/apiError'

const initialState = (): State => ({
  pastOrders: [],
  orderStatuses: [],
  liveOrders: [],
  lastUpdated: {
    pastOrders: undefined,
    orderStatuses: undefined
  },
  lastFailed: {
    pastOrders: undefined,
    orderStatuses: undefined
  },
  fetching: {
    pastOrders: false
  },
  fetchDidFail: {
    pastOrders: false
  }
})

/**
 * @name useOrderHistoryStore
 * @description Store for order history state across the app
 *
 */
const useOrderHistoryStore = defineStore(STORE_ID.ORDER_HISTORY, {
  state: initialState,

  getters: {
    getPastOrderById: (state) => {
      return (redcatOrderId: number): PastOrder | undefined => {
        return state.pastOrders?.find((pastOrder) => pastOrder.redcatOrderId === redcatOrderId)
      }
    },
    /**
     * @description Get past orders excluding the live orders
     * and sort them by orderTime in descending order
     */
    getPastOrders(): PastOrder[][] {
      if (!this.pastOrders) {
        return []
      }

      const pastOrders = this.pastOrders.filter((pastOrder) => !this.isLiveOrder(pastOrder.redcatOrderId!))
      return groupOrdersByDay(pastOrders)
    },
    /**
     * @description Check if the order is in the orderStatuses list
     */
    isLiveOrder: (state) => {
      return (redcatOrderId: number): boolean => {
        return state.orderStatuses.some((orderStatus) => orderStatus.order.redcatOrderId === redcatOrderId)
      }
    },
    /**
     * @description Get the order status by redcatOrderId
     */
    getLiveOrderByRedcatOrderId: (state) => {
      return (redcatOrderId: number): OrderStatus | undefined => {
        return state.orderStatuses.find((orderStatus) => orderStatus.order.redcatOrderId === redcatOrderId)
      }
    },
    /**
     * @description Get the orderId from the orderStatuses list
     */
    getLiveOrderById: (state) => {
      return (orderId: string): OrderStatus | undefined => {
        return state.orderStatuses.find((orderStatus) => orderStatus.id === orderId)
      }
    },
    /**
     * @description Get the live orders (order statuses filtered by live orders)
     * And sort by the liverOrders array
     */
    getLiveOrders(): OrderStatus[] {
      return this.orderStatuses
        .filter((orderStatus) => this.liveOrders.includes(orderStatus.id!))
        .sort((a, b) => {
          return this.liveOrders.indexOf(a.id!) - this.liveOrders.indexOf(b.id!)
        })
    }
  },

  actions: {
    /**
     * @description Fetch the order history of the user
     */
    async fetchOrderHistory() {
      const authStore = useAuthStore()
      if (!authStore.isAuthenticated) {
        throw new Error('User is not authenticated. Could not fetch order history.')
      }

      this.fetching.pastOrders = true
      try {
        this.pastOrders = await getOrderHistory(authStore.authUser!.id!)
        this.lastUpdated.pastOrders = Date.now()
        this.lastFailed.pastOrders = undefined
        this.fetchDidFail.pastOrders = false
      } catch (error: any) {
        this.fetchDidFail.pastOrders = true
        this.lastFailed.pastOrders = Date.now()
        if (isExpectedApiNetworkControlError(error)) {
          return
        }
        throw new Error(`${error.name}: ${error.message}`)
      } finally {
        this.fetching.pastOrders = false
      }
    },
    /**
     * @description Fetch the order status of an order.
     * If the order status is already in the list, update it.
     * If not, add it to the list.
     */
    async fetchOrderStatusById(orderId: string, stage?: number): Promise<void> {
      try {
        const newOrderStatus = await fetchOrderStatus(orderId, stage)

        const index = this.orderStatuses.findIndex((orderStatus) => orderStatus.id === orderId)
        if (index !== -1) {
          this.orderStatuses[index] = newOrderStatus
        } else {
          this.orderStatuses.push(newOrderStatus)
        }
      } catch (error: any) {
        if (error.status === 404) {
          console.warn(`Order status not found for order ${orderId}. Removing from list if present.`)
          this.orderStatuses = this.orderStatuses.filter((orderStatus) => orderStatus.id !== orderId)
          this.performLiveOrderCleanup()
          return
        }

        if (!isExpectedApiNetworkControlError(error)) {
          console.error(`${error.name}: ${error.message}`)
        }
      } finally {
        this.performLiveOrderCleanup()
      }
    },
    async fetchOrderStatuses(): Promise<void> {
      const requests: Array<Promise<void>> = []

      for (const liveOrder of this.liveOrders) {
        requests.push(this.fetchOrderStatusById(liveOrder))
      }

      try {
        await Promise.allSettled(requests)
        this.lastUpdated.orderStatuses = Date.now()
        this.lastFailed.orderStatuses = undefined
      } catch (error: any) {
        this.lastFailed.orderStatuses = Date.now()
        if (!isExpectedApiNetworkControlError(error)) {
          console.error(`${error.name}: ${error.message}`)
        }
      }
    },
    /**
     * @description Set the order history with the orders
     */
    setOrderHistoryWithOrders(orders: PastOrder[]) {
      this.pastOrders = orders
    },
    /**
     * @description Remove a live order from the list
     */
    removeLiveOrder(orderId: string) {
      this.liveOrders = this.liveOrders.filter((id) => id !== orderId)
    },
    /**
     * @description Remove live orders that are no longer valid.
     * This is determined by a live order no longer in the order status list.
     */
    performLiveOrderCleanup() {
      if (!this.liveOrders.length) {
        return
      }

      this.liveOrders = this.liveOrders.filter((liveOrder) => {
        return this.orderStatuses.some((orderStatus) => orderStatus.id === liveOrder)
      })
    }
  },
  persist: true
})

// UTILS
/**
 * @description Sort orders by orderTime in descending order
 */
function sortOrdersByDateTime(orders: PastOrder[], sort: 'asc' | 'desc' = 'desc'): PastOrder[] {
  const filteredOrders = orders
    // disregard orders with no orderTime
    .filter((order) => !!order.orderTime)
    .sort((a, b) => a.orderTime!.localeCompare(b.orderTime!))

  return sort === 'desc' ? filteredOrders.reverse() : filteredOrders
}

/**
 * @description Groups the orders by day
 * @returns groupedOrders {PastOrder[][]} - An array of arrays of PastOrder
 */
function groupOrdersByDay(orders: PastOrder[]): PastOrder[][] {
  const ordersSortedByRecent = sortOrdersByDateTime(orders)

  const ordersGroupedByDay: PastOrder[][] = []

  for (const order of ordersSortedByRecent) {
    let currentDayIndex = ordersGroupedByDay.findIndex((dateGroup) => {
      const thisOrdersDay = new Date(order.orderTime!)
      const thisGroupsDay = new Date(dateGroup[0].orderTime!)
      return isSameDay(thisOrdersDay, thisGroupsDay)
    })

    if (currentDayIndex === -1) {
      ordersGroupedByDay.push([])
      currentDayIndex = ordersGroupedByDay.length - 1
    }

    ordersGroupedByDay[currentDayIndex].push(order)
  }

  return ordersGroupedByDay
}

export default useOrderHistoryStore
export { initialState }
